import { useState, useEffect } from 'react'
import type { FC } from 'react'
import { Box } from '@mui/material'
import IconError from '@mui/icons-material/Error'
import { chatAttachment, CacheRule, ChatAttachmentRetrieveResponse } from '@fireflyhealth/api'

import ChatMessageModal from './ChatMessageModal'
import { Loader } from '~/shared/ui'
import { useQuery } from '~/shared/hooks/query'
interface ChatMessageAttachmentProps {
  attachment: number
  userId?: number
  mine?: boolean
}

export const ChatMessageAttachment: FC<ChatMessageAttachmentProps> = ({ attachment, mine }) => {
  const [modal, setModal] = useState(false)
  const [resolvedImage, setResolvedImage] = useState<ChatAttachmentRetrieveResponse | null>(null)
  const [thumbnailError, setThumbnailError] = useState(false)

  const { data: attachmentResponse, isError: fetchError } = useQuery(
    chatAttachment.retrieveQueryKey({ id: attachment }),
    () => chatAttachment.retrieve({ id: attachment }, { cache: CacheRule.ALWAYS }),
    { staleTime: Infinity },
  )
  const attachmentData = attachmentResponse?.data

  useEffect(() => {
    if (!attachmentData) return
    const thumb = new Image()
    thumb.src = attachmentData.thumbnail
    thumb.onload = () => setResolvedImage(attachmentData)
    thumb.onerror = () => setThumbnailError(true)
    // Clean up event listeners on unmount. Prevents "can't update state on unmounted component" type errors
    return () => {
      thumb.onload = null
      thumb.onerror = null
    }
  }, [attachmentData])

  const error = fetchError || thumbnailError
  const loading = !resolvedImage && !error
  const link = resolvedImage && !error ? resolvedImage.file : '#'

  return (
    // Clicking a chat message selects it, and that handler calls preventDefault().
    // The preview renders in a portal, but React still bubbles its clicks up to the message.
    // Stopping them here lets the preview's link open the full image in a new tab.
    <Box onClick={event => event.stopPropagation()}>
      <ChatMessageModal open={modal} closeModal={() => setModal(false)} image={link} />
      <Box
        component="button"
        onClick={() => {
          if (resolvedImage) setModal(true)
        }}
        {...(error || loading ? {} : { target: '_blank' })}
        sx={theme => ({
          display: 'block',
          cursor: 'pointer',
          position: 'relative',
          width: '25rem',
          height: '25rem',
          border: 0,
          padding: 0,
          borderRadius: '0.4rem',
          overflow: 'hidden',
          marginTop: theme.spacing(0.5),
          background: theme.palette.grey[400],
          color: theme.palette.grey[600],
        })}
      >
        {resolvedImage && (
          <Box
            sx={{
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              height: '100%',
              ...(mine
                ? {}
                : {
                    filter: 'blur(25px)',
                    transition: '100ms linear all',
                    transitionDelay: '250ms',
                    '&:hover': {
                      filter: 'blur(0px)',
                    },
                  }),
            }}
            style={{ backgroundImage: `url(${resolvedImage.thumbnail})` }}
          />
        )}
        {loading && <Loader inline />}
        {error && (
          <IconError
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
        )}
      </Box>
    </Box>
  )
}
